import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, catchError, switchMap, map, take } from 'rxjs/operators';
import {
    Season,
    SeasonChallengesResponse,
    SeasonUnclaimedRewards,
    SeasonLevel,
    SeasonLevelsResponse,
    ActiveSeasonsResponse,
    SeasonProgressResponse,
    SeasonLevelProgress,
    SeasonReward, RewardBase, MOCK_REWARDS,
    SeasonsResponse,
} from '../models/season.model';
import { Rewards } from '../../reward/models/request.model';
import { endpoints } from '../../utils/endpoints';
import { Store } from '@ngrx/store';
import { selectCurrentPlayerId } from '../../profile/store/selectors/profileGeneral.selector';

@Injectable({
  providedIn: 'root'
})
export class SeasonApi {
  private http = inject(HttpClient);
  private store = inject(Store);

  constructor() {}

  fetchSeasons(): Observable<SeasonsResponse> {
    return this.http.get<Season[]>(endpoints.seasons.children.getSeasons()).pipe(
      map(seasons => ({ seasons })),
      catchError(() => {
       return of({ seasons: [] });
      })
    );
  }

  fetchActiveSeasons(): Observable<ActiveSeasonsResponse> {
    return this.http.get<Season[]>(endpoints.seasons.children.getActiveSeasons()).pipe(
      map(seasons => ({ seasons })),
      catchError(() => {
       return of({ seasons: [] });
      })
    );
  }

  fetchSeasonProgress(seasonId: string, profileId?: string): Observable<SeasonProgressResponse> {
    if (profileId) {
      return this.http.get<SeasonProgressResponse>(endpoints.seasons.children.getSeasonProgress(seasonId, profileId));
    }
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(id => {
        if (!id) throw new Error('Profile ID is required for season progress');
        return this.http.get<SeasonProgressResponse>(endpoints.seasons.children.getSeasonProgress(seasonId, id));
      })
    );
  }

  fetchSeasonInfo(seasonId?: string): Observable<Season> {
    if (seasonId) {
      return this.http.get<Season>(endpoints.seasons.children.getSeasonById(seasonId));
    }
    return throwError(() => new Error('Season ID is required'));
  }

  fetchSeasonChallenges(): Observable<SeasonChallengesResponse> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId) return of({ challenges: [] });
        return this.http.get<Rewards[]>(endpoints.challenges.children.getChallenges(profileId)).pipe(
          map(challenges => ({ challenges })),
          catchError(() => of({ challenges: [] }))
        );
      })
    );
  }

  fetchUnclaimedRewards(): Observable<SeasonUnclaimedRewards> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId) return of({ count: 0 });
        return this.http.get<SeasonUnclaimedRewards>(endpoints.seasons.children.getPastSeasonsUnclaimedRewards(profileId));
      }),
      catchError(() => of({ count: 0 }))
    );
  }

  fetchSeasonLevels(seasonId: string): Observable<SeasonLevelsResponse> {
    return this.http.get<SeasonLevel[]>(endpoints.seasons.children.getSeasonLevels(seasonId)).pipe(
      map(levels => ({ levels })),
      catchError((error) => throwError(() => error))
    );
  }

  fetchSeasonLevelsProgress(seasonId: string, profileId: string, fromNumber: number, toNumber: number): Observable<SeasonLevelProgress[]> {
    return this.http.get<SeasonLevelProgress[]>(endpoints.seasons.children.getSeasonLevelsProgress(seasonId, profileId, fromNumber, toNumber));
  }

  claimSeasonReward(rewardIds: string[], profileId: string): Observable<void> {
    return this.http.post<void>(endpoints.seasons.children.claimSeasonRewards(profileId), { rewardIds });
  }

  claimChallenge(challengeId: string): Observable<void> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId) return of(undefined);
        return this.http.post<void>(endpoints.challenges.children.rewardsClaim(profileId), { rewardIds: [challengeId] });
      })
    );
  }

  claimAllUnclaimedRewards(seasonId: string): Observable<RewardBase[]> {
    return this.store.select(selectCurrentPlayerId).pipe(
      take(1),
      switchMap(profileId => {
        if (!profileId || !seasonId) return of([]);
        return this.http.get<RewardBase[]>(endpoints.seasons.children.claimAllUnclaimedRewards(seasonId, profileId));
      })
    );
  }
}
